import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { TOURS } from "../lib/tours";

const PAD = 6, TIP_W = 340;

export default function TourGuide() {
  const location = useLocation();
  const navigate = useNavigate();
  const [steps, setSteps] = useState(null);
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(undefined);
  const activeRoute = useRef(null);
  const tour = TOURS[location.pathname];

  const stop = useCallback((completed) => {
    if (completed && activeRoute.current) localStorage.setItem(`tour_done_${activeRoute.current}`, "1");
    setSteps(null); setIdx(0); setRect(undefined);
  }, []);

  const begin = useCallback((route) => {
    const t = TOURS[route];
    if (!t) return;
    activeRoute.current = route;
    setIdx(0); setRect(undefined); setSteps(t.steps);
  }, []);

  useEffect(() => {
    const p = new URLSearchParams(location.search);
    if (p.get("tour") === "1" && TOURS[location.pathname]) {
      p.delete("tour");
      navigate({ pathname: location.pathname, search: p.toString() }, { replace: true });
      setTimeout(() => begin(location.pathname), 400);
    } else if (steps && activeRoute.current !== location.pathname) {
      stop(false);
    }
  }, [location]);

  useEffect(() => {
    if (!steps) return;
    const step = steps[idx];
    let tries = 0, timer;
    setRect(undefined);
    const attempt = () => {
      if (!step.target) { setRect(null); return; }
      const el = document.querySelector(step.target);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        timer = setTimeout(() => {
          const r = el.getBoundingClientRect();
          setRect({ x: r.left, y: r.top, w: r.width, h: r.height });
        }, 380);
      } else if (tries++ < (idx === 0 ? 10 : 4)) {
        timer = setTimeout(attempt, 300);
      } else if (idx < steps.length - 1) {
        setIdx(i => i + 1);
      } else {
        stop(true);
      }
    };
    attempt();
    const onResize = () => attempt();
    window.addEventListener("resize", onResize);
    return () => { clearTimeout(timer); window.removeEventListener("resize", onResize); };
  }, [steps, idx, stop]);

  if (!steps) {
    if (!tour) return null;
    return (
      <button onClick={() => begin(location.pathname)} data-testid="start-tour-btn"
        className="fixed bottom-5 right-5 z-40 flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[12px] font-semibold text-white shadow-lg transition-transform hover:-translate-y-0.5"
        style={{ background: "linear-gradient(135deg, #0f172a, #334155)" }}>
        <Icons.Compass className="w-3.5 h-3.5" /> Tour this page
      </button>
    );
  }

  const step = steps[idx];
  const measuring = step.target && rect === undefined;
  const centered = rect === null;
  const vw = window.innerWidth, vh = window.innerHeight;
  let tipStyle;
  if (centered) {
    tipStyle = { left: Math.max(12, vw / 2 - TIP_W / 2), top: Math.max(60, vh / 2 - 140) };
  } else if (rect) {
    const below = rect.y + rect.h + 250 < vh;
    let top = below ? rect.y + rect.h + PAD + 12 : rect.y - PAD - 242;
    top = Math.min(Math.max(12, top), vh - 260);
    tipStyle = {
      left: Math.min(Math.max(12, rect.x + rect.w / 2 - TIP_W / 2), vw - TIP_W - 12),
      top,
    };
  }

  return createPortal(
    <div className="fixed inset-0" style={{ zIndex: 10000 }} data-testid="tour-overlay">
      {centered && <div className="absolute inset-0" style={{ background: "rgba(15,23,42,0.62)" }} onClick={() => stop(false)} />}
      {!centered && rect && (
        <div className="absolute" style={{
          left: rect.x - PAD, top: rect.y - PAD, width: rect.w + PAD * 2, height: rect.h + PAD * 2,
          borderRadius: 10, boxShadow: "0 0 0 200vmax rgba(15,23,42,0.62)",
          border: "2px solid rgba(129,140,248,0.9)", transition: "all .28s ease", pointerEvents: "none",
        }} />
      )}
      {measuring && <div className="absolute inset-0" style={{ background: "rgba(15,23,42,0.4)" }} />}
      {!measuring && (
        <div className="absolute rounded-xl p-4 shadow-2xl" style={{ ...tipStyle, width: TIP_W, background: "#fff", border: "1px solid #e2e8f0" }} data-testid="tour-tooltip">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "#4F46E5" }} data-testid="tour-step-count">Step {idx + 1} of {steps.length}</span>
            <button onClick={() => stop(false)} data-testid="tour-close" className="p-0.5 rounded hover:bg-slate-100"><Icons.X className="w-3.5 h-3.5 text-slate-400" /></button>
          </div>
          <div className="text-[13.5px] font-bold text-slate-900 mb-1">{step.title}</div>
          <div className="text-[11.5px] leading-relaxed text-slate-600">{step.body}</div>
          <div className="flex items-center justify-between mt-3.5">
            <div className="flex gap-1">
              {steps.map((_, i) => <span key={i} className="w-1.5 h-1.5 rounded-full" style={{ background: i === idx ? "#4F46E5" : "#e2e8f0" }} />)}
            </div>
            <div className="flex items-center gap-1.5">
              <button onClick={() => stop(false)} data-testid="tour-skip" className="px-2 py-1 text-[11px] font-medium text-slate-400 hover:text-slate-600">Skip</button>
              {idx > 0 && <button onClick={() => setIdx(i => i - 1)} data-testid="tour-back" className="px-2.5 py-1.5 text-[11px] font-semibold rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50">Back</button>}
              {idx < steps.length - 1
                ? <button onClick={() => setIdx(i => i + 1)} data-testid="tour-next" className="px-3 py-1.5 text-[11px] font-semibold rounded-lg text-white" style={{ background: "#4F46E5" }}>Next</button>
                : <button onClick={() => stop(true)} data-testid="tour-finish" className="px-3 py-1.5 text-[11px] font-semibold rounded-lg text-white" style={{ background: "#059669" }}>Finish</button>}
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}
